import { useRef, useState, type PointerEvent } from "react";
import { ImageIcon, RefreshCw, RotateCcw, Upload } from "lucide-react";
import type { SiteNode } from "@zirro/schema";
import { useEditorStore } from "../store";
import { renderedImageRatio } from "../dom";
import { FRAME_RATIOS, IMAGE_TYPES, clampPercent, type FrameRatio } from "../image-frame";

/**
 * Canva-style controls for an Image: replace the photo (it keeps the frame),
 * pick the frame's shape, fill or fit it, drag to choose what stays in view,
 * and zoom in.
 */
export function ImageFrameControls({ node }: { node: SiteNode }) {
  const updateNodeProps = useEditorStore((s) => s.updateNodeProps);
  const replaceImage = useEditorStore((s) => s.replaceImage);
  const uploadImage = useEditorStore((s) => s.uploadImage);
  const openMediaModal = useEditorStore((s) => s.openMediaModal);
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const props = node.props ?? {};
  const src = typeof props.src === "string" && props.src ? props.src : null;
  const ratio = (typeof props.aspectRatio === "string" ? props.aspectRatio : "original") as FrameRatio;
  const ratioValue = FRAME_RATIOS.find((r) => r.id === ratio)?.value ?? null;
  const fit = props.fit === "contain" ? "contain" : "cover";
  const focalX = typeof props.focalX === "number" ? props.focalX : 50;
  const focalY = typeof props.focalY === "number" ? props.focalY : 50;
  const zoom = typeof props.zoom === "number" ? props.zoom : 1;
  // The crop position only matters when part of the photo is hidden.
  const canReposition = Boolean(src) && ((ratioValue !== null && fit === "cover") || zoom > 1);

  const set = (patch: Record<string, unknown>) => updateNodeProps(node.id, patch);
  const replace = (url: string) => {
    setError(null);
    replaceImage(node.id, url, renderedImageRatio(node.id));
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      replace(await uploadImage(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Drag on the preview to move the focal point.
  const dragging = useRef(false);
  const moveFocus = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    set({
      focalX: clampPercent(((e.clientX - rect.left) / rect.width) * 100),
      focalY: clampPercent(((e.clientY - rect.top) / rect.height) * 100),
    });
  };

  return (
    <div className="zirro-image-frame">
      <div
        className={`zirro-image-frame__preview${canReposition ? " zirro-image-frame__preview--movable" : ""}`}
        style={{ aspectRatio: ratioValue ? String(ratioValue) : undefined }}
        onPointerDown={(e) => {
          if (!canReposition) return;
          dragging.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          moveFocus(e);
        }}
        onPointerMove={(e) => dragging.current && moveFocus(e)}
        onPointerUp={() => (dragging.current = false)}
        title={canReposition ? "Drag to choose what stays in view" : undefined}
      >
        {src ? (
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              objectFit: ratioValue ? fit : undefined,
              height: ratioValue ? "100%" : "auto",
              objectPosition: `${focalX}% ${focalY}%`,
              transform: zoom > 1 ? `scale(${zoom})` : undefined,
              transformOrigin: `${focalX}% ${focalY}%`,
            }}
          />
        ) : (
          <span className="zirro-image-frame__empty">
            <ImageIcon size={22} aria-hidden="true" />
            No image yet
          </span>
        )}
        {canReposition ? (
          <span className="zirro-image-frame__focus" style={{ left: `${focalX}%`, top: `${focalY}%` }} aria-hidden="true" />
        ) : null}
      </div>

      <div className="zirro-image-frame__actions">
        <button type="button" className="zirro-btn zirro-btn-primary" onClick={() => openMediaModal(replace)}>
          <RefreshCw size={13} aria-hidden="true" />
          {src ? "Replace" : "Choose image"}
        </button>
        <button
          type="button"
          className="zirro-btn zirro-btn-secondary"
          disabled={uploading}
          onClick={() => fileInput.current?.click()}
        >
          <Upload size={13} aria-hidden="true" />
          {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept={IMAGE_TYPES.join(",")}
          hidden
          onChange={(e) => {
            void upload(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      {error ? <p className="zirro-error-text" role="alert">{error}</p> : null}
      <p className="zirro-hint">Tip: drop a photo from your computer onto any image on the canvas to replace it.</p>

      <div className="zirro-field">
        <span>Frame shape</span>
        <div className="zirro-ratio-grid" role="radiogroup" aria-label="Frame shape">
          {FRAME_RATIOS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={ratio === option.id}
              className="zirro-ratio-chip"
              onClick={() => set({ aspectRatio: option.id })}
              title={option.id === "original" ? "The photo's own shape" : option.id}
            >
              <i
                aria-hidden="true"
                style={{
                  aspectRatio: option.value ? String(option.value) : undefined,
                  ...(option.value
                    ? option.value >= 1
                      ? { width: 18 }
                      : { height: 18 }
                    : { width: 16, height: 12, borderStyle: "dashed" }),
                }}
              />
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {ratioValue !== null ? (
        <div className="zirro-field">
          <span>Fit in frame</span>
          <div className="zirro-segmented-control" role="radiogroup" aria-label="Fit in frame">
            <button
              type="button"
              role="radio"
              aria-checked={fit === "cover"}
              className={`zirro-segment${fit === "cover" ? " active" : ""}`}
              onClick={() => set({ fit: "cover" })}
            >
              Fill frame
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={fit === "contain"}
              className={`zirro-segment${fit === "contain" ? " active" : ""}`}
              onClick={() => set({ fit: "contain", zoom: 1 })}
            >
              Show whole image
            </button>
          </div>
        </div>
      ) : null}

      {fit === "cover" && src ? (
        <label className="zirro-field">
          <span className="zirro-field__row">
            Zoom
            <output>{Math.round(zoom * 100)}%</output>
          </span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => set({ zoom: Number(e.target.value) })}
          />
        </label>
      ) : null}

      {canReposition && (focalX !== 50 || focalY !== 50 || zoom !== 1) ? (
        <button
          type="button"
          className="zirro-btn zirro-btn-secondary zirro-btn--block"
          onClick={() => set({ focalX: 50, focalY: 50, zoom: 1 })}
        >
          <RotateCcw size={13} aria-hidden="true" />
          Reset position and zoom
        </button>
      ) : null}
    </div>
  );
}
